import _ from 'lodash';
import moment from 'moment';
import { useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { convertToMoment } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { getDataRange } from '@/shared/lib/timeRange';
import { TPickDateAction } from '@/shared/model';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectPickedBookmarkDate,
  useReportStore,
} from '@/entities/report';
import type { EcgBookmarkTimeProps } from './ecgBookmarkTime';

/**
 * Drives the modal that bookmarks a single moment of the ECG strip on the beats tab. Holds the date, time and note inputs in merged local state, mirrors the globally picked bookmark date into them, and exposes handlers to jump the viewer, arm pick-on-chart mode, validate the moment against the available data range and append the bookmark to the store.
 *
 * @param props - Modal props carrying the ECG data map and the study timezone.
 * @returns An object carrying state, the picker and note handlers, the jump, pick, cancel and add-bookmark callbacks, and the jumpTimeRef and pickTimeRef anchors.
 */
const useEcgBookmarkTime = ({ ecgDataMap, timezone }: EcgBookmarkTimeProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Writes the beats tab's ECG jump target back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement jump timestamp, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Writes the beats tab's jump portion back into the report store, which decides where in the viewport the jump target lands. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement portion index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Writes the beats tab's pick-on-chart mode back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement pick-date action, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab's modal control flags back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement control-modal flags, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
  const [state, setState] = useMergeState<{
    date: any;
    time: any;
    errorObject: any;
    notes: string;
  }>({
    date: undefined,
    time: undefined,
    errorObject: {},
    notes: '',
  });
  const jumpTimeRef = useRef<HTMLDivElement>(null);
  const pickTimeRef = useRef<HTMLDivElement>(null);

  /**
   * Records the date or time picked for a new ECG bookmark. Writes the incoming moment under the given field name and clears every previously reported validation error.
   *
   * @param name - Local state field the picker writes to, either date or time.
   * @param time - Picked moment value for that field.
   * @param timeString - Formatted text emitted by the picker, accepted but unused.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any, timeString?: string | string[]) => {
    setState({ [name]: time, errorObject: {} });
  };

  /**
   * Moves the ECG viewer to the bookmark's date. Converts the date to epoch milliseconds, writes it as the jump target only when it differs from the current one, and centres the view by setting the jump portion to 2.
   *
   * @param date - Date the ECG viewer should jump to.
   * @returns Nothing.
   */
  const onClickJumpTo = (date: any) => {
    const epochDate = new Date(date).getTime();
    setJumpDate((prev: any) => {
      if (epochDate && epochDate !== prev) {
        return epochDate;
      }
      return prev;
    });
    setJumpPortion(2);
  };

  /**
   * Arms chart picking so the reviewer's next click on the ECG selects the bookmark moment. Sets the tab's pick-date action to the bookmark mode.
   *
   * @returns Nothing.
   */
  const onClickPickOn = () => {
    setPickDateAction(TPickDateAction.BOOKMARK);
  };

  /**
   * Records the free-text note typed for a new ECG bookmark. Writes the value under the given field name and clears every previously reported validation error.
   *
   * @param name - Local state field the input writes to, in practice notes.
   * @param value - Text entered by the reviewer.
   * @returns Nothing.
   */
  const onChangeNotes = (name: string, value: any) => {
    setState({ [name]: value, errorObject: {} });
  };

  /**
   * Keeps typing inside the bookmark note field from reaching the ECG viewer's keyboard shortcuts. Stops the key event from propagating any further.
   *
   * @param e - Keyboard event raised by the note input.
   * @returns Nothing.
   */
  const onKeyDownInput = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Discards the bookmark being composed and closes its modal. Clears the validation errors and the note text, then resets the tab's modal control flags with isShowAddEcgBookmark off.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({ errorObject: {}, notes: '' });
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddEcgBookmark: false });
  };

  /**
   * Validates the picked moment and appends a ten-second ECG bookmark centred on it. Marks an empty date or time invalid and aborts, rejects a moment falling in a no-data gap with an error toast, otherwise pushes a bookmark spanning five seconds either side of the picked time together with the typed note and closes the modal.
   *
   * @returns Nothing.
   */
  const onClickAddBookmark = () => {
    const clonedErrorObject = { ...state.errorObject };
    if (!state.date) {
      _.assign(clonedErrorObject, { date: 'Invalid value' });
    } else {
      delete clonedErrorObject.date;
    }
    if (!state.time) {
      _.assign(clonedErrorObject, { time: 'Invalid value' });
    } else {
      delete clonedErrorObject.time;
    }
    if (!_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject });
      return;
    }
    const pickedBookmarkDateConvert = moment(pickedBookmarkDate);
    const dataRange = getDataRange(
      ecgDataMap,
      pickedBookmarkDateConvert,
      pickedBookmarkDateConvert,
    );
    if (_.isEmpty(dataRange)) {
      toastrError('It is not possible to bookmark time in no data area.', 'Error');
      const clonedErrorObject = { ...state.errorObject };
      _.assign(clonedErrorObject, {
        time: 'Invalid value',
        date: 'Invalid value',
      });
      setState({ errorObject: clonedErrorObject });
      return;
    }
    const bookmark = {
      start: pickedBookmarkDateConvert.clone().add(-5, 'seconds').toISOString(),
      stop: pickedBookmarkDateConvert.clone().add(5, 'seconds').toISOString(),
      center: pickedBookmarkDateConvert.valueOf(),
      notes: state.notes,
    };
    // bookmark omits IEcgBookmark fields this atom's type (Recoil/index.ts, Task 7) requires (e.g. id) - pre-existing.
    setEcgBookmarks((prev: any) => [...prev, bookmark]);
    onClickCancel();
  };

  useUpdateEffect(() => {
    let convertedDateMoment;
    if (pickedBookmarkDate) {
      convertedDateMoment = convertToMoment(pickedBookmarkDate, timezone);
    }
    setState({
      date: convertedDateMoment,
      time: convertedDateMoment,
      errorObject: {},
    });
  }, [pickedBookmarkDate]);

  return {
    state,
    onChange,
    onClickJumpTo,
    onClickPickOn,
    onChangeNotes,
    onKeyDownInput,
    onClickCancel,
    onClickAddBookmark,
    jumpTimeRef,
    pickTimeRef,
  };
};

export default useEcgBookmarkTime;
